/**
 * This file defines the GoProDialog component, which renders a form dialog allowing users
 * to submit a request to upgrade their Insight subscription type to Pro.
 */
import PropTypes from 'prop-types';
import * as React from 'react';
import { DialogInput } from './DialogInput';
import { useForm } from '../../hooks/form';
import { FormContainer } from './FormContainer';
import PhoneNumberInput from './PhoneNumberInput';
import { useUser, useUserFields } from '../../hooks/user';
import { getUserActionSubmitConfig } from '../../functions/forms';
import { UserActionType } from '../menus/menuItemsConfig';
import theme from '../../styles/theme';
import { DialogContent, Fade, Link, Typography } from '@mui/material';
import { matchIsValidTel } from 'mui-tel-input';
import { Link as RouterLink } from 'react-router-dom';
import { Stack } from '@mui/system';
import { CloseButton } from '../buttons/CloseButton';
import { forwardRef, useEffect, useState } from 'react';
import { InsightProDetails } from '../proShowcase/InsightProDetails';
import { InsightLogo } from '../logos/InsightLogo';

const requiredFields = ['phoneNumber'];

const validationConfig = {
    phoneNumber: {
        validate: matchIsValidTel
    }
};

/** Renders the Insight Pro logo for the form title */
const formTitle = (
    <InsightLogo
        fontSize={25}
        color={theme.palette.common.black}
        subType='pro'
    />
);

//slower fade-in transition when dialog is opened
const SlowFade = forwardRef((props, ref) => (
    <Fade {...props} ref={ref} timeout={{ enter: 1000, exit: 300 }} />
));

SlowFade.displayName = 'SlowFade';

/**
 * Component for submitting requests to upgrade to Essentia Insight Pro via a form dialog interface.
 * Initially renders a preview page showcasing details about Insight Pro allowing the user to navigate to the main form.
 * The form renders user information input fields, prefilling user details where appropriate, validates input,
 * and handles form submission. It also displays a dialog when the form has been submitted successfully.
 *
 * @component
 * @param {boolean} open - Indicates if the dialog is open.
 * @param {Function} onFormClose - Function to close the GoPro dialog.
 * @param {Function} onSubmissionViewClose - Function called when Submission View close button selected
 * @returns {React.ReactElement} The rendered GoProDialog component
 */
const GoProDialog = ({ open, onFormClose, onSubmissionViewClose }) => {
    const formId = UserActionType.GO_PRO;

    const { getFirstName, getLastName, getEmail } = useUserFields();
    const { user } = useUser();
    const initialFormState = {
        fullName: { value: `${getFirstName()} ${getLastName()}` },
        phoneNumber: {
            countryCode: '1',
            value: '+1',
            error: false,
            valid: true,
            clicked: false
        },
        referralCode: { value: '' }
    };

    const submitConfig = getUserActionSubmitConfig(formId, user);

    const [insightProDetailsOpen, setInsightProDetailsOpen] = useState(true);

    useEffect(() => {
        if (open) {
            setInsightProDetailsOpen(true);
        }
    }, [open]);

    const {
        formState,
        resetFields,
        handleSend,
        submissionState,
        setPhoneNumber,
        setFormField,
        isErrorAndClicked,
        setFormState
    } = useForm(
        initialFormState,
        requiredFields,
        validationConfig,
        submitConfig
    );

    /**
     * Renders form inputs for user information and contact details.
     */
    const formFields = (
        <Stack sx={{ height: '100%', justifyContent: 'space-between' }}>
            <Stack spacing={3} padding={2}>
                <DialogInput
                    id='firstName'
                    label='First name'
                    value={getFirstName()}
                    data-analyticsid='first-name'
                    disabled
                />
                <DialogInput
                    id='lastName'
                    label='Last name'
                    value={getLastName()}
                    data-analyticsid='last-name'
                    disabled
                />
                <DialogInput
                    id='email'
                    label='Email'
                    value={getEmail()}
                    data-analyticsid='email'
                    disabled
                />
                <PhoneNumberInput
                    id='phoneNumber'
                    value={formState.phoneNumber.value}
                    onChange={setPhoneNumber}
                    required
                    helperText={
                        isErrorAndClicked('phoneNumber')
                            ? 'This field is mandatory'
                            : 'We will only use this to call you about this request'
                    }
                    error={isErrorAndClicked('phoneNumber')}
                    valid={formState.phoneNumber.valid}
                    onBlur={() =>
                        setFormState({
                            ...formState,
                            phoneNumber: {
                                ...formState.phoneNumber,
                                clicked: true,
                                error:
                                    formState.phoneNumber.value ===
                                    `+${formState.phoneNumber.countryCode}`
                            }
                        })
                    }
                    placeholder='Phone number *'
                    data-analyticsid='phone-number'
                />
                <DialogInput
                    id='referralCode'
                    value={formState.referralCode.value}
                    onChange={(e) =>
                        setFormField('referralCode', e.target.value)
                    }
                    label='Referral code'
                    inputProps={{ maxLength: 100 }}
                    data-analyticsid='referral-code'
                />
                <Typography
                    variant='smallprint'
                    textAlign='center'
                    sx={{ pl: 2.5, pr: 2.5 }}
                >
                    Provide your details and we&apos;ll be in touch with a quick
                    demo. Per our{' '}
                    <Link
                        component={RouterLink}
                        to='/privacy-policy'
                        target='_blank'
                        data-analyticsid='privacy-policy'
                    >
                        privacy policy
                    </Link>
                    , we will not share your data with third parties.
                </Typography>
            </Stack>
        </Stack>
    );

    const submissionView = (
        <DialogContent
            sx={{ background: theme.palette.background.modalGradientBlue }}
            data-analyticsid='go-pro-confirmation'
        >
            <Stack sx={{ height: '100%', alignItems: 'flex-end' }}>
                <CloseButton
                    color={theme.palette.common.white}
                    onClose={onSubmissionViewClose}
                />
                <Stack
                    sx={{
                        height: '100%',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-evenly'
                    }}
                >
                    <InsightLogo
                        fontSize={74}
                        color={theme.palette.common.white}
                        subType='pro'
                    />
                    <Typography
                        varient='body1'
                        sx={{
                            color: theme.palette.font.white,
                            textAlign: 'center',
                            padding: 4
                        }}
                    >
                        Thank you for registering interest in Essentia Insight
                        Pro
                        <br />
                        <br />
                        We will be in touch with you soon
                    </Typography>
                </Stack>
            </Stack>
        </DialogContent>
    );

    const previewPage = (
        <InsightProDetails
            onClose={onFormClose}
            onGoProClick={() => setInsightProDetailsOpen(false)}
        />
    );

    return (
        <FormContainer
            formId={formId}
            formState={formState}
            open={open}
            handleClose={onFormClose}
            title={formTitle}
            submissionView={submissionView}
            requiredFields={requiredFields}
            handleSend={handleSend}
            submissionState={submissionState}
            resetFields={resetFields}
            previewPage={previewPage}
            previewPageOpen={insightProDetailsOpen}
            disableStandardSubmissionView
            TransitionComponent={SlowFade}
            PaperProps={
                insightProDetailsOpen
                    ? {
                          style: {
                              background:
                                  theme.palette.background.modalGradientBlue
                          }
                      }
                    : {}
            }
            dataAnalyticsId={
                insightProDetailsOpen ? 'go-pro-showcase' : 'go-pro-form'
            }
        >
            {formFields}
        </FormContainer>
    );
};

GoProDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onFormClose: PropTypes.func.isRequired,
    onSubmissionViewClose: PropTypes.func
};

export default GoProDialog;
